/**
 * Utility functions for determining chat colors based on conversation type
 */

export type ChatType = 'talent' | 'business' | 'support' | 'project' | 'general' | 'internal';

/**
 * Determines the chat type based on conversation data
 */
export function getChatType(conversation: any): ChatType {
  // Check conversation category first
  const category = conversation.conversation_category || conversation.conversationCategory;
  
  if (category === 'application') return 'talent';
  if (category === 'hiring') return 'business';
  if (category === 'support') return 'support';
  if (category === 'project') return 'project';
  if (category === 'internal') return 'internal';
  
  // Check conversation type
  const type = conversation.type || conversation.conversation_type;
  if (type === 'business') return 'business';
  
  // Check for specific identifiers
  if (conversation.job_application_id) return 'talent';
  if (conversation.ticket_id || conversation.ticket_number) return 'support';
  if (conversation.project_id || conversation.project_name) return 'project';
  
  // Default to general
  return 'general';
}

/**
 * Gets CSS classes for chat colors based on conversation type
 */
export function getChatColorClasses(conversation: any): {
  background: string;
  foreground: string;
  accent: string;
} {
  const chatType = getChatType(conversation);
  
  const colorMap = {
    talent: {
      background: 'bg-chat-talent',
      foreground: 'text-chat-talent-foreground',
      accent: 'bg-chat-talent-accent'
    },
    business: {
      background: 'bg-chat-business',
      foreground: 'text-chat-business-foreground',
      accent: 'bg-chat-business-accent'
    },
    support: {
      background: 'bg-chat-support',
      foreground: 'text-chat-support-foreground',
      accent: 'bg-chat-support-accent'
    },
    project: {
      background: 'bg-chat-project',
      foreground: 'text-chat-project-foreground',
      accent: 'bg-chat-project-accent'
    },
    general: {
      background: 'bg-chat-general',
      foreground: 'text-chat-general-foreground',
      accent: 'bg-chat-general-accent'
    },
    internal: {
      background: 'bg-chat-internal',
      foreground: 'text-chat-internal-foreground',
      accent: 'bg-chat-internal-accent'
    }
  };
  
  return colorMap[chatType];
}

/**
 * Gets CSS variable name for chat colors
 */
export function getChatColorVariables(conversation: any): {
  background: string;
  foreground: string;
  accent: string;
} {
  const chatType = getChatType(conversation);
  
  return {
    background: `--chat-${chatType}`,
    foreground: `--chat-${chatType}-foreground`,
    accent: `--chat-${chatType}-accent`
  };
}

/**
 * Gets a readable name for the chat type
 */
export function getChatTypeName(conversation: any): string {
  const chatType = getChatType(conversation);
  
  const nameMap = {
    talent: 'Application',
    business: 'Hiring',
    support: 'Support',
    project: 'Project',
    general: 'General',
    internal: 'Internal'
  };
  
  return nameMap[chatType];
}